import {
  Accordion,
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Progress,
  Stack,
  Text,
  VStack,
  Separator,
} from '@chakra-ui/react'
import { getTrackPathPrefix, useContentTrackContext } from '@src/components/sco/Education/ContentTrackContext'
import { SectionStepsList } from '@src/components/sco/Education/ContentTrackSectionDetail/SectionStepsList'
import { PaywalledAction } from '@src/components/sco/PaywalledAction'
import { ChevronRightIcon } from '@src/components/sco/icons'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { ContentTrack } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import React, { useMemo } from 'react'

type ContentTrackDashboardProps = {
  contentTrack: ContentTrack
}
export const ContentTrackDashboard = (props: ContentTrackDashboardProps) => {
  const { getBreakpointValue } = useBreakpointContext()
  const trackContext = useContentTrackContext()
  const progress = trackContext.contentTrackEnrollment?.progress

  const sectionPercents: number[] = progress?.sectionStatuses?.map(x => x.completionAsPercent) || []
  const countSectionsComplete = sectionPercents.filter(x => x >= 95).length
  const countSections = sectionPercents.length
  const trackPercentComplete = trackContext.contentTrackEnrollment?.progress?.completionAsPercent || 0

  const nextHref = useMemo(() => {
    let href = `/${getTrackPathPrefix(props.contentTrack)}/${props.contentTrack.slug}/${
      props.contentTrack.sections[0]?.slug
    }/${props.contentTrack.sections[0]?.steps[0]?.slug}`
    if (progress) {
      const nextSectionProgress = progress.sectionStatuses.find(x => x.completionAsPercent < 95)
      const nextStepProgress = nextSectionProgress?.stepStatuses.find(x => x.completionAsPercent < 95)

      if (nextSectionProgress && nextStepProgress) {
        const nextSection = props.contentTrack.sections.find(x => x.id === nextSectionProgress.targetId)
        if (nextSection) {
          const nextStep = nextSection.steps.find(x => x.id === nextStepProgress.targetId)
          if (nextStep) {
            href = `/${getTrackPathPrefix(props.contentTrack)}/${props.contentTrack.slug}/${
              nextSection.slug
            }/${nextStep.slug}`
          }
        }
      }
    }

    return href
  }, [progress])

  return (
    <VStack
      align={'center'}
      width={'full'}
      padding={getBreakpointValue([4, 8, 20])}
      // bg={`linear-gradient(to bottom, ${props.contentTrack.color?.hex}50, transparent 500px)`}
    >
      {/* HERO */}
      <Flex
        height={getBreakpointValue(['35vh', '35vh', '35vh', undefined])}
        justify={'center'}
        width={'full'}
      >
        <Stack
          align={'center'}
          direction={'row'}
          gap={20}
          justify={'center'}
          maxWidth={'container.xl'}
          width={'full'}
        >
          <VStack align={'flex-start'} flex={1} gap={8} justify={'center'}>
            <Heading size={getBreakpointValue(['4xl'])}>{props.contentTrack.title}</Heading>
            <PaywalledAction
              contentPermission={'canAccessCourses'}
              title={'Become a member to get the full course & progress tracking.'}
              upgradeCtaLabel={'Join to Unlock'}
              variant={'boxed'}
              upgradeBoxProps={{
                bgColor: 'blackAlpha.500',
                padding: '1.5em',
              }}
              upgradeCtaButtonProps={{
                variant: 'outline',
              }}
            >
              <VStack align={'flex-start'} flexGrow={1} gap={5} width={'full'}>
                <Text textStyle={'labelLg'}>
                  {countSectionsComplete}
                  {' of '}
                  {countSections} lessons completed
                  {' • '}
                  {trackPercentComplete}%
                </Text>
                <Progress.Root
                  colorPalette={'cyan'}
                  value={Math.max(trackPercentComplete, 5)}
                  variant={'subtle'}
                  width={'full'}
                >
                  <Progress.Track>
                    <Progress.Range />
                  </Progress.Track>
                </Progress.Root>
              </VStack>
            </PaywalledAction>
            <Button asChild size={getBreakpointValue(['sm', 'md', 'lg'])} variant={'cta'}>
              <NextLink href={nextHref}>
                {trackPercentComplete > 0 ? 'Resume Course' : 'Start Course'}
                <ChevronRightIcon />
              </NextLink>
            </Button>
          </VStack>
          {props.contentTrack.featuredImage?.url && (
            <Flex align={'center'} display={getBreakpointValue(['none', 'none', 'none', 'initial'])}>
              <Image
                maxHeight={'25em'}
                objectFit={'contain'}
                src={props.contentTrack.featuredImage?.url}
                transition={'all 0.1s ease-in'}
                zIndex={1}
              />
            </Flex>
          )}
        </Stack>
      </Flex>
      {/** OVERVIEW */}
      <VStack align={'center'} width={'full'}>
        <VStack align={'stretch'} gap={10} maxWidth={'container.xl'} width={'full'}>
          <Heading size={'3xl'}>Course Overview</Heading>
          {props.contentTrack.shortDescription && (
            <VStack align={'flex-start'} gap={4}>
              <Text textStyle={'badgeXs'}>About this course</Text>
              <Text fontSize={getBreakpointValue(['lg', 'lg', 'xl'])}>
                {props.contentTrack.shortDescription}
              </Text>
            </VStack>
          )}
          <Separator borderColor={'backgroundTertiary'} />
          {props.contentTrack.sections.map((section, index) => (
            <Accordion.Root key={index} collapsible multiple defaultValue={['item-0']}>
              <Accordion.Item border={'none'} boxShadow={'none'} value={'item-0'}>
                <Accordion.ItemTrigger
                  _expanded={{ color: 'white' }}
                  _hover={{ bgColor: 'none', color: 'white' }}
                  padding={0}
                >
                  <Box as={'span'} flex={1} textAlign={'left'}>
                    <Heading size={['xl', 'xl']}>{section.title}</Heading>
                  </Box>
                  <Accordion.ItemIndicator />
                </Accordion.ItemTrigger>
                <Accordion.ItemContent marginTop={5} padding={0}>
                  <Accordion.ItemBody>
                    <SectionStepsList
                      contentTrackSection={section}
                      contentTrackSectionId={section.id}
                      contentTrackStepId={section.steps[0].id}
                      contentTrackSteps={section.steps}
                    />
                  </Accordion.ItemBody>
                </Accordion.ItemContent>
              </Accordion.Item>
            </Accordion.Root>
          ))}
        </VStack>
      </VStack>
    </VStack>
  )
}
ContentTrackDashboard.fragments = {
  ContentTrack: () => gql`
    fragment _ on ContentTrack {
      id
      title
      featuredImage {
        id
        url(transformation: { image: { resize: { fit: max, width: 1000 } } })
      }
      shortDescription
      terms {
        id
        name
      }
      slug
      type
      color {
        hex
      }
      sections {
        id
        title
        slug
        steps {
          id
          slug
          title
        }
        ${SectionStepsList.fragments.ContentTrackSection()}
      }
    }
  `,
}
